import React from "react";
import { Box } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { StewartTableColumn } from "controls/global/stewart-table/StewartTable";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { colors } from "theme/defaultStyle";
import {
  BuildPolicyColumnsOptions,
  HOI_FILTER_OPTIONS,
  SafeAutocomplete,
  fieldErrorCell,
  formatCurrency,
  formatDate,
  formatRemitSplitPct,
  formatTransCodes,
  toIsoDate,
  toIsoDateMidnight,
} from "../shared/policyColumnHelpers";
import InputSearch from "../quality-review/InputSearch";
import SelectSearch from "../shared/SelectSearch";
import DatePickerSearch from "controls/global/datepicker-search/DatePickerSearch";
import FlagBadges from "../shared/FlagBadges";
import ValidationPill from "../shared/ValidationPill";

export const CQ_GRID_NAME = "PolicyManagementCQ";

export function buildCqColumns(
  opts: BuildPolicyColumnsOptions
): StewartTableColumn<PolicyManagementRecord>[] {
  const {
    stateOptions,
    productTypeOptions,
    recordTypeOptions,
    propertyTypeOptions,
    countyOptions,
    formPrefixOptions,
    transCodeOptions,
    policyFormNameAsync,
    fileNumberAsync,
    originalPolicyNumberAsync,
    onFiltersChange,
    onMultiSelectChange,
    onRowAction,
    headerFilterDefaults,
  } = opts;

  return [
            {
      field: "BatchNumber",
      name: "Batch Number",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 1,
      actionComponent: onFiltersChange
        ? () => <InputSearch name="BatchNumber" onChange={onFiltersChange} />
        : undefined,
      valueGetter: (row) => <TooltipCell title={row.BatchNumber || ""} />,
    },
    {
      field: "BatchSeqNumber",
      name: "Batch Sequence Number",
      width: 200,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 2,
      actionComponent: onFiltersChange
        ? () => <InputSearch name="BatchSeqNumber" onChange={onFiltersChange} />
        : undefined,
      valueGetter: (row) => <TooltipCell title={row.BatchSeqNumber || ""} />,
    },
    {
      field: "PolicyNumber",
      name: "Policy Number",
      width: 252,
      minWidth: 252,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 3,
      // Pin to the left, just past the 100px row-header (checkbox/expand) column.
      sticky: true,
      left: 100,
      actionComponent: onFiltersChange
        ? () => <InputSearch name="PolicyNumberList" onChange={onFiltersChange} />
        : undefined,
      valueGetter: (row: PolicyManagementRecord) => (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {row.PolicyNumber || ""}
          </Box>
          <FlagBadges record={row} />
        </Box>
      ),
    },
    {
      field: "AgentID",
      name: "Agent ID",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 4,
      actionComponent: onFiltersChange
        ? () => <InputSearch name="AgentIDList" defaultValue={headerFilterDefaults?.AgentIDList} onChange={onFiltersChange} />
        : undefined,
      valueGetter: (row) => fieldErrorCell(row, "AgentID", row.AgentID || ""),
    },
    {
      field: "ReportingID",
      name: "Reporting ID",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 5,
      actionComponent: onFiltersChange
        ? () => <InputSearch name="ReportingID" defaultValue={headerFilterDefaults?.ReportingID} onChange={onFiltersChange} />
        : undefined,
      valueGetter: (row) => <TooltipCell title={row.ReportingID || ""} />,
    },
    {
      field: "StateAbbr",
      name: "State",
      width: 100,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 6,
      actionComponent:
        stateOptions && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="StateAbbrList"
                options={stateOptions}
                onChange={onMultiSelectChange}
                showValueAsTag
                popperMinWidth={260}
              />
            )
          : undefined,
      valueGetter: (row) => <TooltipCell title={row.StateAbbr || ""} />,
    },
    {
      field: "CountyCode",
      name: "County",
      width: 250,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 7,
      actionComponent:
        countyOptions && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="CountyList"
                options={countyOptions}
                onChange={onMultiSelectChange}
                filterOptionsLimit={254}
              />
            )
          : undefined,
      valueGetter: (row) => fieldErrorCell(row, "CountyCodeNum", row.CountyDisplayName || ""),
    },
    {
      field: "MunicipalityNum",
      name: "Municipality",
      width: 160,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 8,
      actionComponent: onFiltersChange
        ? () => <InputSearch name="MunicipalityNum" onChange={onFiltersChange} />
        : undefined,
      valueGetter: (row) => <TooltipCell title={row.MunicipalityNum || ""} />,
    },
    {
      field: "IsHOI",
      name: "HOI",
      width: 100,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 9,
      actionComponent: onFiltersChange
        ? () => (
            <SelectSearch
              name="IsHOI"
              options={HOI_FILTER_OPTIONS}
              onChange={onFiltersChange}
            />
          )
        : undefined,
      valueGetter: (row) => <TooltipCell title={row.IsHOI ? "Yes" : "No"} />,
    },
    {
      field: "OriginalPolicyNumber",
      name: "Original Policy Number",
      width: 250,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 10,
      actionComponent:
        originalPolicyNumberAsync && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="OriginalPolicyNumberList"
                options={originalPolicyNumberAsync.options}
                loading={originalPolicyNumberAsync.loading}
                onInputChange={originalPolicyNumberAsync.onInputChange}
                onChange={onMultiSelectChange}
              />
            )
          : undefined,
      valueGetter: (row) => (
        <TooltipCell title={row.OriginalPolicyNumber || ""} />
      ),
    },
    {
      field: "PolicyFormName",
      name: "Policy Form Name",
      width: 200,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 11,
      actionComponent:
        policyFormNameAsync && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="PolicyFormNameList"
                options={policyFormNameAsync.options}
                loading={policyFormNameAsync.loading}
                onInputChange={policyFormNameAsync.onInputChange}
                onChange={onMultiSelectChange}
              />
            )
          : undefined,
      valueGetter: (row) => fieldErrorCell(row, "PolicyFormName", row.PolicyFormName || ""),
    },
    {
      field: "ProductTypeAbbrTypeName",
      name: "Policy Type",
      width: 220,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 12,
      actionComponent:
        productTypeOptions && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="ProductTypeAbbrTypeCodeList"
                options={productTypeOptions}
                onChange={onMultiSelectChange}
              />
            )
          : undefined,
      valueGetter: (row) => (
        <TooltipCell
          title={
            row.ProductTypeAbbrTypeName ||
            row.ProductTypeAbbrTypeCode ||
            ""
          }
        />
      ),
    },
    {
      field: "ReceivedDate",
      name: "Received Date",
      width: 180,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 13,
      actionComponent: onFiltersChange
        ? () => (
            <DatePickerSearch
              header
              size="small"
              handleDateChange={(date) =>
                onFiltersChange("ReceivedDate", date ? toIsoDateMidnight(date) : null)
              }
            />
          )
        : undefined,
      valueGetter: (row) => <TooltipCell title={formatDate(row.ReceivedDate)} />,
    },
    {
      field: "EffectiveDate",
      name: "Effective Date",
      width: 180,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 14,
      actionComponent: onFiltersChange
        ? () => (
            <DatePickerSearch
              header
              size="small"
              handleDateChange={(date) =>
                onFiltersChange("EffectiveDate", date ? toIsoDate(date) : null)
              }
            />
          )
        : undefined,
      valueGetter: (row) => fieldErrorCell(row, "AccountingPeriod", formatDate(row.EffectiveDate)),
    },
    {
      field: "FileNumber",
      name: "File Name/Number",
      width: 200,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 15,
      actionComponent:
        fileNumberAsync && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="FileNumberList"
                options={fileNumberAsync.options}
                loading={fileNumberAsync.loading}
                onInputChange={fileNumberAsync.onInputChange}
                onChange={onMultiSelectChange}
              />
            )
          : undefined,
      valueGetter: (row) => fieldErrorCell(row, "FileNumber", row.FileNumber || ""),
    },
    {
      field: "FormPrefix",
      name: "Form Prefix",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 16,
      actionComponent:
        formPrefixOptions && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="FormPrefixList"
                options={formPrefixOptions}
                onChange={onMultiSelectChange}
              />
            )
          : undefined,
      valueGetter: (row) => fieldErrorCell(row, "FormPrefix", row.FormPrefix || ""),
    },
    {
      field: "TransCode",
      name: "Trans Code",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 17,
      actionComponent:
        transCodeOptions && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="TransCodeList"
                options={transCodeOptions}
                onChange={onMultiSelectChange}
                filterOptionsLimit={50}
              />
            )
          : undefined,
      valueGetter: (row) => fieldErrorCell(row, "TransCode", formatTransCodes(row)),
    },
    {
      field: "RecordTypeName",
      name: "Record Type",
      width: 180,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 18,
      actionComponent:
        recordTypeOptions && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="RecordTypeCodeList"
                options={recordTypeOptions}
                onChange={onMultiSelectChange}
              />
            )
          : undefined,
      valueGetter: (row) => (
        <TooltipCell
          title={row.RecordTypeName || row.RecordTypeCode || ""}
        />
      ),
    },
    {
      field: "PropertyTypeName",
      name: "Property Type",
      width: 180,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 19,
      actionComponent:
        propertyTypeOptions && onMultiSelectChange
          ? () => (
              <SafeAutocomplete
                name="PropertyTypeCodeList"
                options={propertyTypeOptions}
                onChange={onMultiSelectChange}
              />
            )
          : undefined,
      valueGetter: (row) =>
        fieldErrorCell(
          row,
          "PropertyTypeCode",
          row.PropertyTypeName || row.PropertyTypeCode || ""
        ),
    },
    {
      field: "BookedTypeCode",
      name: "Booked",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 20,
      valueGetter: (row) => <TooltipCell title={row.BookedTypeName || ""} />,
    },
    {
      field: "OpenErrorCount",
      name: "Validation",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 21,
      valueGetter: (row) => {
        const n = row.OpenErrorCount ?? 0;
        return n > 0 ? <ValidationPill state="errors" errorCount={n} /> : null;
      },
    },
    {
      field: "IsEdited",
      name: "Status",
      width: 110,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 22,
      valueGetter: (row) => (
        <TooltipCell title={row.IsEdited ? "Edited" : "Unedited"} />
      ),
    },
    {
      field: "LiabilityAmount",
      name: "Liability",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 23,
      actionComponent: onFiltersChange
        ? () => (
            <InputSearch
              name="LiabilityAmount"
              numeric
              onChange={onFiltersChange}
            />
          )
        : undefined,
      valueGetter: (row) => (
        <Box sx={{ textAlign: "right" }}>
          <TooltipCell title={formatCurrency(row.LiabilityAmount)} />
        </Box>
      ),
    },
    {
      field: "OriginalRemitSplitPct",
      name: "Orig Split %",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 24,
      valueGetter: (row) => (
        <Box sx={{ textAlign: "right" }}>
          <TooltipCell title={formatRemitSplitPct(row.OriginalRemitSplitPct)} />
        </Box>
      ),
    },
    {
      field: "NewRemitSplitPct",
      name: "New Split %",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 25,
      valueGetter: (row) => (
        <Box sx={{ textAlign: "right" }}>
          <TooltipCell title={formatRemitSplitPct(row.NewRemitSplitPct)} />
        </Box>
      ),
    },
    {
      field: "ActualFee",
      name: "Premium/Fee",
      width: 140,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 26,
      valueGetter: (row) => (
        <Box sx={{ textAlign: "right" }}>
          <TooltipCell title={formatCurrency(row.ActualFee)} />
        </Box>
      ),
    },
    {
      field: "ActualRemittance",
      name: "UW Remittance",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 27,
      valueGetter: (row) => (
        <Box sx={{ textAlign: "right" }}>
          <TooltipCell title={formatCurrency(row.ActualRemittance)} />
        </Box>
      ),
    },
    {
      field: "uniqueIdentifier",
      name: "Actions",
      width: 90,
      sortable: false,
      resizable: false,
      draggable: false,
      // Close Queue adds Split % + financial columns at positions 21-25, so
      // Actions sits past them to stay the last column.
      position: 28,
      // Lock to the right edge so the row action stays visible while scrolling.
      sticky: true,
      right: 0,
      valueGetter: (row) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            cursor: onRowAction ? "pointer" : "default",
            color: colors.blue01,
          }}
          onClick={onRowAction ? () => onRowAction(row) : undefined}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </Box>
      ),
    },	
  ];
}
